import { useState } from 'react';
import { Link } from 'react-router-dom';
import HeroSlider from './components/HeroSlider';
import MovieCard from '@/components/feature/MovieCard';
import { genres, decades } from '@/mocks/movies';
import { getLatestMovies, getHotMovies, getGenreCount } from '@/utils/movie';

function SectionHeading({ eyebrow, title, action, actionLabel }: { eyebrow: string; title: string; action?: string; actionLabel?: string }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
      <div>
        <p className="text-xs font-semibold tracking-[0.25em] text-accent-700 uppercase mb-2">{eyebrow}</p>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{title}</h2>
      </div>
      {action && (
        <Link
          to={action}
          className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-600 whitespace-nowrap cursor-pointer"
        >
          {actionLabel}
          <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const [openSynopsis, setOpenSynopsis] = useState<string | null>(null);
  const latest = getLatestMovies(8);
  const hot = getHotMovies(5);

  return (
    <>
      <HeroSlider />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="w-full">
          <SectionHeading eyebrow="Browse by" title="分类快捷入口" action="/category" actionLabel="全部分类" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1 rounded-lg bg-primary-900 p-6 text-background-50">
              <h3 className="font-heading text-lg font-semibold mb-1">按年代浏览</h3>
              <p className="text-sm text-primary-200/80 mb-5">按上映年代快速定位作品</p>
              <div className="space-y-2.5">
                {decades.map((d) => (
                  <Link
                    key={d.label}
                    to={`/category?decade=${d.start}-${d.end}`}
                    className="flex items-center justify-between px-4 py-3 rounded-md bg-primary-800 hover:bg-primary-700 transition-colors cursor-pointer"
                  >
                    <span className="text-sm font-medium">{d.label}</span>
                    <i className="ri-arrow-right-s-line text-accent-400 text-xl"></i>
                  </Link>
                ))}
                <Link
                  to="/category"
                  className="flex items-center justify-between px-4 py-3 rounded-md bg-accent-500 text-primary-950 hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  <span className="text-sm font-bold">全部年代</span>
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">按类型浏览</h3>
              <p className="text-sm text-foreground-500 mb-5">选择你的观影偏好</p>
              <div className="flex flex-wrap gap-2.5">
                {genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-accent-300 hover:bg-accent-50 transition-colors cursor-pointer"
                  >
                    {g}
                    <span className="text-xs text-foreground-400">{getGenreCount(g)}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 最新上线 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="w-full">
          <SectionHeading eyebrow="New Releases" title="最新上线电影" action="/category?sort=latest" actionLabel="查看全部" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latest.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </div>
      </section>

      {/* 精选热门 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="w-full">
          <SectionHeading eyebrow="Featured & Hot" title="精选推荐 · 热门影片" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-4">
              {hot.map((m) => (
                <div key={m.id} className="flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200">
                  <Link to={`/detail/${m.id}`} className="shrink-0 w-24 h-36 rounded-md overflow-hidden bg-background-200 cursor-pointer">
                    <img src={m.poster} alt={`${m.title} 海报`} title={`${m.title} + 李采潭电影`} className="w-full h-full object-cover object-top" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/detail/${m.id}`} className="font-heading text-lg font-bold text-foreground-950 hover:text-primary-700 transition-colors cursor-pointer">
                        {m.title}
                      </Link>
                      <span className="text-xs text-foreground-500">{m.year}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
                      <span className="flex items-center gap-1 text-accent-700 font-semibold">
                        <i className="ri-star-fill text-[10px]"></i>{m.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-fire-line text-accent-500"></i>热度 {m.popularity}
                      </span>
                    </div>
                    <p className={`text-xs text-foreground-600 leading-relaxed mt-2 ${openSynopsis === m.id ? '' : 'line-clamp-2'}`}>
                      {m.synopsis}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => setOpenSynopsis(openSynopsis === m.id ? null : m.id)}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary-50 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-100 transition-colors cursor-pointer"
                      >
                        <i className={openSynopsis === m.id ? 'ri-eye-off-line' : 'ri-file-text-line'}></i>
                        {openSynopsis === m.id ? '收起简介' : '剧情简介'}
                      </button>
                      <Link
                        to={`/detail/${m.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-background-200 text-foreground-800 text-xs font-medium whitespace-nowrap hover:bg-background-300 transition-colors cursor-pointer"
                      >
                        详情 <i className="ri-arrow-right-s-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-lg bg-primary-900 p-6 md:p-7 text-background-50 flex flex-col">
              <h3 className="font-heading text-xl font-bold mb-2">关于李采潭电影</h3>
              <p className="text-sm text-primary-200/90 leading-relaxed">
                本站专注于整理与介绍李采潭主演及相关影视作品，覆盖剧情片、爱情片、悬疑犯罪片等多种类型，
                为影迷提供从片名、年份到详细剧情简介的完整资料，帮助你在最短时间内找到想看的那一部。
              </p>
              <div className="grid grid-cols-3 gap-4 mt-7">
                <div>
                  <p className="font-heading text-3xl font-bold text-accent-400">16</p>
                  <p className="text-xs text-primary-200/80 mt-1">收录作品</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-accent-400">12</p>
                  <p className="text-xs text-primary-200/80 mt-1">影片类型</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-accent-400">8.4</p>
                  <p className="text-xs text-primary-200/80 mt-1">平均评分</p>
                </div>
              </div>
              <div className="mt-auto pt-7">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  了解更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}